import { DateTime } from 'luxon';
import {
  DocumentType,
  GenericDocumentType,
  ProviderDocumentType,
  DriverDocumentType,
  VehicleDocumentType,
} from '../data/models/document-type.model';
import {
  Document,
  GenericDocument,
  ProviderDocument,
  DriverDocument,
  VehicleDocument,
} from '../data/models/document.model';

export interface DocumentWrapper {
  id: number;
  documentTypeID: number;
  documentTypeName: string;
  required: boolean;
  effectiveDatesRequired: boolean;
  expiring: boolean;
  expired: boolean;
  document?: GenericDocument;
}

class DocumentUtils {
  private getProviderDocumentType(
    document: ProviderDocument,
    documentTypes: ProviderDocumentType[],
  ): string {
    const typeName = documentTypes.find(
      (type) =>
        document.transportationProviderDocumentTypeID ===
        type.transportationProviderDocumentTypeID,
    )?.transportationProviderDocumentTypeName;
    return typeName || '';
  }

  private getDriverDocumentType(
    document: DriverDocument,
    documentTypes: DriverDocumentType[],
  ): string {
    const typeName = documentTypes.find(
      (type) => document.driverDocumentTypeID === type.driverDocumentTypeID,
    )?.driverDocumentTypeName;
    return typeName || '';
  }

  private getVehicleDocumentType(
    document: VehicleDocument,
    documentTypes: VehicleDocumentType[],
  ): string {
    const typeName = documentTypes.find(
      (type) => document.vehicleDocumentTypeID === type.vehicleDocumentTypeID,
    )?.vehicleDocumentTypeName;
    return typeName || '';
  }

  private isExpired(docType: DocumentType, doc: Document): boolean {
    if (doc.effectiveEndDate) {
      const exp = DateTime.fromISO(doc.effectiveEndDate);
      const now = DateTime.now();
      const start = doc.effectiveStartDate
        ? DateTime.fromISO(doc.effectiveStartDate)
        : now.minus({ day: 1 });
      return !(now > start && now < exp);
    }
    return false;
  }

  private isExpiring(
    docType: GenericDocumentType,
    doc: GenericDocument,
  ): boolean {
    if (doc.effectiveEndDate) {
      const exp = DateTime.fromISO(doc.effectiveEndDate).endOf('day');
      const now = DateTime.now().startOf('day');
      const diffInDays = exp.diff(now, 'days').days;
      return diffInDays > 0 && diffInDays < 31;
    }
    return false;
  }

  private getWrapperID(
    docType: GenericDocumentType,
    document?: GenericDocument,
  ): number {
    return docType.documentTypeID * 1000000 + (document?.documentID || 0);
  }

  public getDocumentWrappers(
    documentTypes: GenericDocumentType[],
    documents: GenericDocument[],
  ): DocumentWrapper[] {
    const wrappers: DocumentWrapper[] = [];
    documentTypes.forEach((dt) => {
      const docs = documents.filter(
        (d) => d.documentTypeID === dt.documentTypeID,
      );
      if (docs.length > 0) {
        docs.forEach((d) => {
          wrappers.push({
            id: this.getWrapperID(dt, d),
            documentTypeID: dt.documentTypeID,
            documentTypeName: dt.documentTypeName,
            required: dt.isRequired,
            effectiveDatesRequired: dt.isEffectiveDatesRequired,
            expiring: this.isExpiring(dt, d),
            expired: this.isExpired(dt, d),
            document: d,
          });
        });
      } else if (dt.isRequired && dt.isActive) {
        wrappers.push({
          id: this.getWrapperID(dt),
          documentTypeID: dt.documentTypeID,
          documentTypeName: dt.documentTypeName,
          required: dt.isRequired,
          effectiveDatesRequired: dt.isEffectiveDatesRequired,
          expiring: false,
          expired: false,
        });
      }
    });
    return wrappers;
  }

  public providerTypesToGenericTypes(
    providerDocumentTypes: ProviderDocumentType[],
  ): GenericDocumentType[] {
    return providerDocumentTypes.map((type) => {
      return {
        ...type,
        documentTypeID: type.transportationProviderDocumentTypeID,
        documentTypeName: type.transportationProviderDocumentTypeName,
      };
    });
  }

  public driverTypesToGenericTypes(
    driverDocumentTypes: DriverDocumentType[],
  ): GenericDocumentType[] {
    return driverDocumentTypes.map((type) => {
      return {
        ...type,
        documentTypeID: type.driverDocumentTypeID,
        documentTypeName: type.driverDocumentTypeName,
      };
    });
  }

  public vehicleTypesToGenericTypes(
    vehicleDocumentTypes: VehicleDocumentType[],
  ): GenericDocumentType[] {
    return vehicleDocumentTypes.map((type) => {
      return {
        ...type,
        documentTypeID: type.vehicleDocumentTypeID,
        documentTypeName: type.vehicleDocumentTypeName,
      };
    });
  }

  public providerToGeneric(
    providerDocuments: ProviderDocument[],
    providerDocumentTypes: ProviderDocumentType[],
  ): GenericDocument[] {
    return providerDocuments.map((doc) => {
      return {
        ...doc,
        documentID: doc.transportationProviderDocumentID
          ? doc.transportationProviderDocumentID
          : 0,
        documentTypeID: doc.transportationProviderDocumentTypeID,
        documentTypeName: this.getProviderDocumentType(
          doc,
          providerDocumentTypes,
        ),
      };
    });
  }

  public driverToGeneric(
    driverDocuments: DriverDocument[],
    driverDocumentTypes: DriverDocumentType[],
  ): GenericDocument[] {
    return driverDocuments.map((doc) => {
      return {
        ...doc,
        documentID: doc.driverDocumentID ? doc.driverDocumentID : 0,
        documentTypeID: doc.driverDocumentTypeID,
        documentTypeName: this.getDriverDocumentType(doc, driverDocumentTypes),
      };
    });
  }

  public vehicleToGeneric(
    vehicleDocuments: VehicleDocument[],
    vehcileDocumentTypes: VehicleDocumentType[],
  ): GenericDocument[] {
    return vehicleDocuments.map((doc) => {
      return {
        ...doc,
        documentID: doc.vehicleDocumentID ? doc.vehicleDocumentID : 0,
        documentTypeID: doc.vehicleDocumentTypeID,
        documentTypeName: this.getVehicleDocumentType(
          doc,
          vehcileDocumentTypes,
        ),
      };
    });
  }

  public hasAllRequiredDocuments(types: DocumentType[], documents: Document[]) {
    const requiredTypes = (types || []).filter(
      (t) => t.isActive && t.isRequired,
    );
    const allDocuments = documents || [];

    if (allDocuments.length < requiredTypes.length) {
      return false;
    }
    let hasAll = true;

    // Go through each required type and make sure there is at least one unexpired
    // document of that type. If the document is expired, it doesn't matter unless
    // the effective dates are required.
    requiredTypes.every((t) => {
      const typeId =
        (t as ProviderDocumentType).transportationProviderDocumentTypeID ||
        (t as DriverDocumentType).driverDocumentTypeID ||
        (t as VehicleDocumentType).vehicleDocumentTypeID;
      const docs = allDocuments.filter((d) => {
        const docTypeId =
          (d as ProviderDocument).transportationProviderDocumentTypeID ||
          (d as DriverDocument).driverDocumentTypeID ||
          (d as VehicleDocument).vehicleDocumentTypeID;
        return docTypeId === typeId;
      });
      if (docs.length) {
        let hasValid = false;
        docs.forEach((d) => {
          hasValid =
            hasValid || !t.isEffectiveDatesRequired || !this.isExpired(t, d);
        });
        hasAll = hasAll && hasValid;
      } else {
        hasAll = false;
      }
      return hasAll;
    });
    return hasAll;
  }
}

const documentUtils = new DocumentUtils();

export default documentUtils;
